import React from 'react'
import { Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import Indicator from '../condition/indicators/types/Indicator'
import { COMPOSITE_LIMITS, CompositeOutput } from '../../../types/composite'
import { CompositeValidationIssue } from '../../../common/utils/composite'
import { DraftStep } from './types'
import StepRefSelect from './StepRefSelect'
import { INVALID_BORDER_COLOR, INVALID_GLOW, INVALID_TRANSITION } from './invalidStyles'

interface OutputsEditorProps {
  outputs: CompositeOutput[]
  onChange: (outputs: CompositeOutput[]) => void
  steps: DraftStep[]
  getIndicatorByValue: (value: string) => Indicator | undefined
  issuesByOutput?: Map<number, CompositeValidationIssue[]>
}

const OutputsEditor: React.FC<OutputsEditorProps> = ({
  outputs,
  onChange,
  steps,
  getIndicatorByValue,
  issuesByOutput,
}) => {
  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <Box>
        <Typography variant="subtitle1">Outputs</Typography>
        <Typography variant="body2" color="text.secondary">
          Exposed values become the indicator's selectable outputs in conditions.
        </Typography>
      </Box>
      {outputs.map((output, index) => {
        const issues = issuesByOutput?.get(index) ?? []
        const hasError = issues.some((issue) => issue.level === 'error')
        return (
          <Box
            key={index}
            sx={{
              // Same invalid treatment as a step card / pattern condition:
              // the border only appears when the row is at fault
              border: '1px solid',
              borderColor: hasError ? INVALID_BORDER_COLOR : 'transparent',
              boxShadow: hasError ? INVALID_GLOW : 'none',
              transition: INVALID_TRANSITION,
              borderRadius: 2,
              px: hasError ? 1 : 0,
              py: hasError ? 1 : 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <StepRefSelect
                steps={steps}
                beforeIndex={steps.length}
                value={output.ref}
                onChange={(ref) =>
                  ref &&
                  'node' in ref &&
                  onChange(outputs.map((o, i) => (i === index ? { ...o, ref } : o)))
                }
                getIndicatorByValue={getIndicatorByValue}
                allowSources={false}
                label="Step"
              />
              <TextField
                size="small"
                label="Output name"
                value={output.field}
                onChange={(e) =>
                  onChange(
                    outputs.map((o, i) => (i === index ? { ...o, field: e.target.value } : o))
                  )
                }
                sx={{ width: 180 }}
              />
              <Tooltip title="Remove output">
                <span>
                  <IconButton
                    size="small"
                    disabled={outputs.length <= 1}
                    onClick={() => onChange(outputs.filter((_, i) => i !== index))}
                  >
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            {issues.map((issue, issueIndex) => (
              <Typography
                key={issueIndex}
                variant="caption"
                sx={{ color: issue.level === 'error' ? INVALID_BORDER_COLOR : 'warning.main' }}
              >
                {issue.message}
              </Typography>
            ))}
          </Box>
        )
      })}
      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          disabled={outputs.length >= COMPOSITE_LIMITS.maxOutputs || steps.length === 0}
          onClick={() => {
            // First unused valueN — a count-based name collides after deletes
            const used = new Set(outputs.map((o) => o.field))
            let n = 1
            while (used.has(`value${n}`)) n += 1
            onChange([
              ...outputs,
              { field: `value${n}`, ref: { node: steps[steps.length - 1].id } },
            ])
          }}
        >
          Add output
        </Button>
      </Box>
    </Box>
  )
}

export default OutputsEditor
